import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import { useCreateCustomer } from "hooks";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import { getPhoneValidation } from "constants/validate";

const CreateCustomer = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["customer"]);
  const { addToast } = useToasts();

  const getInitDefaultValues = () => {
    return {
      name: "",
      code: "",
      phone: "",
      mail: "",
      note: "",
      // ward_id: undefined,
      // district_id: undefined,
      // province_id: undefined,
    };
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    mail: yup.string().max(250, t("form.validate.char_max", { max: 250 })),
    note: yup.string().max(250, t("form.validate.char_max", { max: 250 })),
    phone: yup
      .string()
      .nullable()
      .matches(getPhoneValidation(t).regex, getPhoneValidation(t).errorMessage)
      .when(getPhoneValidation(t).when),
    // ward_id: yup
    //   .object()
    //   .required(t("form.validate.select", { title: t("form.ward_id") })),
    // district_id: yup
    //   .object()
    //   .required(t("form.validate.select", { title: t("form.district_id") })),
    // province_id: yup
    //   .object()
    //   .required(t("form.validate.select", { title: t("form.province_id") })),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // Create Customer
  const onSuccessCreateCustomer = _data => {
    navigate("/customer/list");
  };
  const onErrorCreateCustomer = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { isLoading, mutate: createCustomer } = useCreateCustomer(
    onSuccessCreateCustomer,
    onErrorCreateCustomer
  );

  const onSubmit = values => {
    const data = getValues();
    createCustomer({
      name: data.name,
      code: data.code,
      phone: data.phone,
      mail: data.mail,
      note: data.note,
      // district_id: data.district_id.value,
      // province_id: data.province_id.value,
      // ward_id: data.ward_id.value,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.manager") },
              { name: t("title.list"), link: "/customer/list" },
              { name: t("title.create") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="row-cols-1 row-cols-md-2">
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="code">
                          {t("form.code")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "code"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.code"),
                          })}
                          {...register("code")}
                        />
                        {errors.code && (
                          <MessageFormValidate error={errors.code.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="name">
                          {t("form.name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "name"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.name"),
                          })}
                          {...register("name")}
                        />
                        {errors.name && (
                          <MessageFormValidate error={errors.name.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="phone">{t("form.phone")}</Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "phone"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.phone"),
                          })}
                          {...register("phone")}
                        />
                        {errors.phone && (
                          <MessageFormValidate error={errors.phone.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="mail">{t("form.mail")}</Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "mail"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.mail"),
                          })}
                          {...register("mail")}
                        />
                        {errors.mail && (
                          <MessageFormValidate error={errors.mail.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="note">{t("form.note")}</Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "note"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.note"),
                          })}
                          {...register("note")}
                        />
                        {errors.note && (
                          <MessageFormValidate error={errors.note.message} />
                        )}
                      </FormGroup>
                    </Col>
                    {/*<Col>*/}
                    {/*  <FormGroup className="mb-3">*/}
                    {/*    <Label htmlFor="ward_id">*/}
                    {/*      {t("form.ward_id")}{" "}*/}
                    {/*      <span className="text-danger">*</span>*/}
                    {/*    </Label>*/}
                    {/*    <Controller*/}
                    {/*      control={control}*/}
                    {/*      name="ward_id"*/}
                    {/*      render={({ field }) => (*/}
                    {/*        <Select*/}
                    {/*          {...field}*/}
                    {/*          placeholder={t("placeholder.select", {*/}
                    {/*            title: t("form.ward_id"),*/}
                    {/*          })}*/}
                    {/*          options={[*/}
                    {/*            { value: "11001", label: "Phường 7" },*/}
                    {/*            { value: "11002", label: "Phường 1" },*/}
                    {/*            { value: "11003", label: "Phường 8" },*/}
                    {/*          ]}*/}
                    {/*          classNamePrefix="select2-selection"*/}
                    {/*        />*/}
                    {/*      )}*/}
                    {/*    />*/}
                    {/*    {errors.ward_id && (*/}
                    {/*      <MessageFormValidate error={errors.ward_id.message} />*/}
                    {/*    )}*/}
                    {/*  </FormGroup>*/}
                    {/*</Col>*/}
                    {/*<Col>*/}
                    {/*  <FormGroup className="mb-3">*/}
                    {/*    <Label htmlFor="district_id">*/}
                    {/*      {t("form.district_id")}{" "}*/}
                    {/*      <span className="text-danger">*</span>*/}
                    {/*    </Label>*/}
                    {/*    <Controller*/}
                    {/*      control={control}*/}
                    {/*      name="district_id"*/}
                    {/*      render={({ field }) => (*/}
                    {/*        <Select*/}
                    {/*          {...field}*/}
                    {/*          placeholder={t("placeholder.select", {*/}
                    {/*            title: t("form.district_id"),*/}
                    {/*          })}*/}
                    {/*          options={[*/}
                    {/*            { value: "552", label: "Quận 1" },*/}
                    {/*            { value: "553", label: "Quận 12" },*/}
                    {/*            { value: "560", label: "Quận Phú Nhuận" },*/}
                    {/*          ]}*/}
                    {/*          classNamePrefix="select2-selection"*/}
                    {/*        />*/}
                    {/*      )}*/}
                    {/*    />*/}
                    {/*    {errors.district_id && (*/}
                    {/*      <MessageFormValidate*/}
                    {/*        error={errors.district_id.message}*/}
                    {/*      />*/}
                    {/*    )}*/}
                    {/*  </FormGroup>*/}
                    {/*</Col>*/}
                    {/*<Col>*/}
                    {/*  <FormGroup className="mb-3">*/}
                    {/*    <Label htmlFor="province_id">*/}
                    {/*      {t("form.province_id")}{" "}*/}
                    {/*      <span className="text-danger">*</span>*/}
                    {/*    </Label>*/}
                    {/*    <Controller*/}
                    {/*      control={control}*/}
                    {/*      name="province_id"*/}
                    {/*      render={({ field }) => (*/}
                    {/*        <Select*/}
                    {/*          {...field}*/}
                    {/*          placeholder={t("placeholder.select", {*/}
                    {/*            title: t("form.province_id"),*/}
                    {/*          })}*/}
                    {/*          options={[*/}
                    {/*            { value: "1", label: "Thành phố Hà Nội" },*/}
                    {/*            { value: "50", label: "Thành phố Hồ Chí Minh" },*/}
                    {/*          ]}*/}
                    {/*          classNamePrefix="select2-selection"*/}
                    {/*        />*/}
                    {/*      )}*/}
                    {/*    />*/}
                    {/*    {errors.province_id && (*/}
                    {/*      <MessageFormValidate*/}
                    {/*        error={errors.province_id.message}*/}
                    {/*      />*/}
                    {/*    )}*/}
                    {/*  </FormGroup>*/}
                    {/*</Col>*/}
                  </Row>
                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link
                        to="/customer/list"
                        className="btn btn-danger btn-label"
                      >
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>

                    <Col className="col-auto">
                      <Button
                        color="primary"
                        className="btn-label"
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                        disabled={isLoading}
                      >
                        <i className="bx bx-save label-icon"></i>
                        {t("form.save")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateCustomer;
